import React, { createContext, useState, useEffect } from "react";
import { getProperties } from "../application/getProperties";

export const HouseContext = createContext();

const HouseContextProvider = ({ children }) => {
  const [houses, setHouses] = useState([]); // Lista filtrada actual
  const [allHouses, setAllHouses] = useState([]); // Todas las casas sin filtrar
  const [country, setCountry] = useState("Todas las localidades");
  const [countries, setCountries] = useState([]);
  const [property, setProperty] = useState("Todas las Propiedades");
  const [properties, setProperties] = useState([]);
  const [price, setPrice] = useState("Rango de Precio");
  const [state, setState] = useState("Todos los estados");
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  // True after the initial getProperties() resolves. Used by the route guard
  // to show the spinner only on the first load, not on filter changes.
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    const fetchHouses = async () => {
      try {
        const data = await getProperties();

        // Asignar los datos a allHouses y houses
        setAllHouses(data);
        setHouses(data);

        // Extraer y establecer valores únicos para filtros
        const allCountries = data.map((house) => house.country);
        const uniqueCountries = [
          "Todas las localidades",
          ...new Set(allCountries),
        ];
        setCountries(uniqueCountries);

        const allProperties = data.map((house) => house.type);
        const uniqueProperties = [
          "Todas las Propiedades",
          ...new Set(allProperties),
        ];
        setProperties(uniqueProperties);

        const allStates = data.map((house) => house.state);
        const uniqueStates = ["Todos los estados", ...new Set(allStates)];
        setStates(uniqueStates);
      } catch (error) {
        console.error("Error al obtener casas:", error);
      } finally {
        setInitialLoaded(true);
      }
    };

    fetchHouses();
  }, []);

  // Aplica los filtros actuales sobre allHouses y devuelve la lista resultante.
  const applyFilters = () => {
    const minPrice = parseInt(price.split(" ")[0]);
    const maxPrice = parseInt(price.split(" ")[2]);
    return allHouses.filter((house) => {
      const housePrice = parseInt(house.price);
      return (
        (country === "Todas las localidades" || house.country === country) &&
        (property === "Todas las Propiedades" || house.type === property) &&
        (state === "Todos los estados" || house.state === state) &&
        (price === "Rango de Precio" ||
          (housePrice >= minPrice && housePrice <= maxPrice))
      );
    });
  };

  // Filtrado reactivo: apenas cambia cualquier filtro (operación, tipo,
  // localidad o precio) se actualiza la lista, sin necesidad de tocar la lupa.
  useEffect(() => {
    setHouses(applyFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, property, state, price, allHouses]);

  // Se mantiene para el botón de búsqueda (muestra un breve loading).
  const handleClick = () => {
    setLoading(true);
    const newHouses = applyFilters();
    setTimeout(() => {
      setHouses(newHouses);
      setLoading(false);
    }, 600);
  };

  // Función para resetear filtros y mostrar todas las casas
  const handleResetFilters = () => {
    setCountry("Todas las localidades");
    setProperty("Todas las Propiedades");
    setState("Todos los estados");
    setPrice("Rango de Precio");
    setHouses(allHouses); // Mostrar todas las casas
  };

  const removeHouse = (idFirebase) => {
    setAllHouses((prev) => prev.filter((h) => h.idFirebase !== idFirebase));
    setHouses((prev) => prev.filter((h) => h.idFirebase !== idFirebase));
  };

  const refreshHouses = async () => {
    try {
      const data = await getProperties();
      setAllHouses(data);
      setHouses(data);
    } catch (error) {
      console.error("Error al refrescar propiedades:", error);
    }
  };

  return (
    <HouseContext.Provider
      value={{
        country,
        setCountry,
        countries,
        property,
        setProperty,
        properties,
        price,
        setPrice,
        state,
        setState,
        states,
        handleClick,
        handleResetFilters,
        houses,
        allHouses,
        removeHouse,
        refreshHouses,
        loading,
        initialLoaded,
      }}
    >
      {children}
    </HouseContext.Provider>
  );
};

export default HouseContextProvider;
